/**
 * Physics state for the PvP roulette wheel.
 * Encapsulates all SPINNING/TRICKS/SLOWING fields and their mutation methods.
 * No Pixi objects — only numbers, flags, and physics computations.
 */

import {
  computeSpinningVelocity as physicsComputeSpinningVelocity,
  computeSlowingPosition,
  type FinalizeState,
} from "./wheelPhysics"
import { getPvpRouletteRuntime } from "@/shared/lib/pvp-roulette-runtime/runtime"

export type { FinalizeState }

// Must match SPINNING_RAMP_EASE_EXP in WheelEngine.ts.
const SPINNING_RAMP_EASE_EXP = 3

export class WheelPhysicsState {
  // ── SPINNING ──────────────────────────────────────────────────────────────
  spinStartedAt = 0
  spinDurationMs = 0
  isSpinningPhase = false
  spinAngularVelDegPerMs = 0
  scheduledTricksAtMs: number | null = null

  // ── TRICKS ────────────────────────────────────────────────────────────────
  tricksPhaseStartTime = 0
  tricksPhaseStartTimeWallMs = 0
  tricksDurationMs = 0
  tricksEntryVelDegPerMs = 0
  tricksEndFromFallback = false
  scheduledFinalizeAtMs: number | null = null
  scheduledFinalizeTargetDeg: number | null = null
  scheduledFinalizeDurationMs: number | null = null

  // ── SLOWING ───────────────────────────────────────────────────────────────
  finalizeState: FinalizeState | null = null
  isFinalizing = false

  // ── Trick overlays ────────────────────────────────────────────────────────
  trickPermanentOffsetDeg = 0
  trickSpeedBoost: {
    remainingMs: number
    durationMs: number
    peakDegPerMs: number
  } | null = null
  trickSpeedDampen: { remainingMs: number; durationMs: number } | null = null

  // ── Base rotation ─────────────────────────────────────────────────────────
  /** Accumulated rotation in degrees — does NOT include trick push overlay. */
  currentRotationDeg = 0

  // ── Phase control ─────────────────────────────────────────────────────────
  /** True when SPINNING/TRICKS — wheel should advance on each tick. */
  allowIdleSpin = false
  roundResolved = false

  // ── Tick timing ───────────────────────────────────────────────────────────
  lastTickTime = 0

  // ─────────────────────────────────────────────────────────────────────────

  /** Soft reset — called between rounds (keeps field declarations intact). */
  reset(): void {
    this.roundResolved = false
    this.finalizeState = null
    this.isFinalizing = false
    this.isSpinningPhase = false
    this.spinAngularVelDegPerMs = 0
    this.currentRotationDeg = 0
    this.trickPermanentOffsetDeg = 0
    this.trickSpeedBoost = null
    this.trickSpeedDampen = null
  }

  /**
   * Route detach: stop spin/tweens/trick overlays but keep wheel angle for fast-path remount.
   */
  pauseForNavigationDetach(preservedRotationDeg: number): void {
    this.finalizeState = null
    this.isFinalizing = false
    this.isSpinningPhase = false
    this.spinAngularVelDegPerMs = 0
    this.currentRotationDeg = preservedRotationDeg
    this.trickPermanentOffsetDeg = 0
    this.trickSpeedBoost = null
    this.trickSpeedDampen = null
    this.spinStartedAt = 0
    this.spinDurationMs = 0
    this.tricksPhaseStartTime = 0
    this.tricksPhaseStartTimeWallMs = 0
    this.tricksDurationMs = 0
    this.tricksEntryVelDegPerMs = 0
    this.tricksEndFromFallback = false
    this.scheduledTricksAtMs = null
    this.scheduledFinalizeAtMs = null
    this.scheduledFinalizeTargetDeg = null
    this.scheduledFinalizeDurationMs = null
    this.allowIdleSpin = false
    this.roundResolved = false
  }

  /** Hard reset — called by WheelEngine.destroy(). Clears all physics fields. */
  resetAll(): void {
    this.reset()
    this.spinStartedAt = 0
    this.spinDurationMs = 0
    this.tricksPhaseStartTime = 0
    this.tricksPhaseStartTimeWallMs = 0
    this.tricksDurationMs = 0
    this.tricksEntryVelDegPerMs = 0
    this.tricksEndFromFallback = false
    this.scheduledTricksAtMs = null
    this.scheduledFinalizeAtMs = null
    this.scheduledFinalizeTargetDeg = null
    this.scheduledFinalizeDurationMs = null
    this.allowIdleSpin = false
    this.lastTickTime = 0
  }

  // ─── Velocity helpers ────────────────────────────────────────────────────

  /** During FREEZE, scale signed angular velocity by TRICK_FREEZE_FACTOR (same direction). */
  applyTrickFreezeSpeedScale(vel: number): number {
    if (!this.trickSpeedDampen) return vel
    return vel * getPvpRouletteRuntime().TRICK_FREEZE_FACTOR
  }

  /**
   * Velocity during SPINNING or TRICKS phase (deg/ms).
   * `nowMs` is wall-clock time for TRICKS decay alignment.
   */
  computeSpinningVelocity(now: number, nowMs: number): number {
    const rt = getPvpRouletteRuntime()
    return physicsComputeSpinningVelocity({
      state: {
        spinStartedAt: this.spinStartedAt,
        spinDurationMs: this.spinDurationMs,
        tricksPhaseStartTime: this.tricksPhaseStartTime,
        tricksPhaseStartTimeWallMs: this.tricksPhaseStartTimeWallMs,
        tricksDurationMs: this.tricksDurationMs,
        tricksEntryVelDegPerMs: this.tricksEntryVelDegPerMs,
      },
      nowPerfMs: now,
      nowWallMs: nowMs,
      spinSign: rt.WHEEL_VISUAL_SPIN_SIGN,
      peakRevPerSec: rt.SPINNING_PEAK_REV_PER_SEC,
      tricksStartRevPerSec: rt.TRICKS_START_REV_PER_SEC,
      tricksEndRevPerSec: rt.TRICKS_END_REV_PER_SEC,
      rampUpMs: rt.SPINNING_RAMP_UP_MS,
      rampEaseExp: SPINNING_RAMP_EASE_EXP,
      tricksDecayExp: rt.TRICKS_DECAY_EXPONENT,
    })
  }

  /** Base idle spin velocity (SPINNING/TRICKS) with freeze factor applied. */
  getIdleSpinAngularVelDegPerMs(now: number, nowMs: number): number {
    return this.applyTrickFreezeSpeedScale(
      this.computeSpinningVelocity(now, nowMs)
    )
  }

  /**
   * Total instantaneous angular velocity (deg/ms): base spin + push boost overlay, with freeze scaling.
   * Used for diagnostics and doFinalizeToTarget v0 sampling.
   */
  getVisualAngularVelDegPerMs(now: number, nowMs: number): number {
    const s = getPvpRouletteRuntime().WHEEL_VISUAL_SPIN_SIGN
    let vel = 0
    if (this.isSpinningPhase) {
      vel += this.computeSpinningVelocity(now, nowMs)
    }
    if (this.trickSpeedBoost) {
      const { remainingMs, durationMs, peakDegPerMs } = this.trickSpeedBoost
      const k = Math.max(0, Math.min(1, remainingMs / durationMs))
      vel += s * peakDegPerMs * k
    }
    return this.applyTrickFreezeSpeedScale(vel)
  }

  // ─── Trick overlay baking ────────────────────────────────────────────────

  /**
   * Fold trick overlay into base rotation before SLOWING starts.
   * Only the already-accumulated trickPermanentOffsetDeg is baked — no speculative future area.
   * The remaining push boost velocity is captured in velDegPerMs (getVisualAngularVelDegPerMs),
   * which solveFinalizeDelta uses to pick the right number of extra rotations, preserving visual
   * continuity: SLOWING starts from the actual current visual position without a positional jump.
   */
  bakeTrickOverlayIntoBaseRotation(): void {
    this.currentRotationDeg += this.trickPermanentOffsetDeg
    this.trickPermanentOffsetDeg = 0
    this.trickSpeedBoost = null
    this.trickSpeedDampen = null
  }

  // ─── Duration helpers ────────────────────────────────────────────────────

  /**
   * Retune TRICKS duration while keeping velocity continuous.
   * Idempotent: skips if window barely changed (WS echo without real extension).
   */
  updateTricksDurationKeepingVelocity(
    nextDurationFromStartMs: number,
    nowWallMs: number,
    currentVelDegPerMs: number
  ): void {
    const nextDur = Math.max(1, nextDurationFromStartMs)
    if (
      this.tricksPhaseStartTimeWallMs > 0 &&
      Math.abs(nextDur - this.tricksDurationMs) <
        getPvpRouletteRuntime().TRICKS_WALL_DURATION_RETUNE_MIN_DELTA_MS
    ) {
      return
    }
    this.tricksDurationMs = nextDur
    if (this.tricksPhaseStartTimeWallMs <= 0) return

    const s = getPvpRouletteRuntime().WHEEL_VISUAL_SPIN_SIGN
    const signedTricksEnd =
      s * ((getPvpRouletteRuntime().TRICKS_END_REV_PER_SEC * 360) / 1000)
    const elapsedWall = Math.max(0, nowWallMs - this.tricksPhaseStartTimeWallMs)
    const t = Math.min(1, Math.max(0, elapsedWall / this.tricksDurationMs))
    const p = Math.pow(t, getPvpRouletteRuntime().TRICKS_DECAY_EXPONENT)

    if (p >= 0.999) {
      this.tricksEntryVelDegPerMs = currentVelDegPerMs
      this.spinAngularVelDegPerMs = currentVelDegPerMs
      return
    }

    const nextEntry =
      (currentVelDegPerMs - signedTricksEnd * p) / Math.max(1e-6, 1 - p)
    if (Number.isFinite(nextEntry)) {
      this.tricksEntryVelDegPerMs = nextEntry
      this.spinAngularVelDegPerMs = nextEntry
    }
  }

  /**
   * Resolve SLOWING duration: explicit override → persisted server value → TRICKS window estimate.
   * Never returns 1ms unless nothing else is available (prevents collapsed ease-out).
   */
  resolveFinalizeDurationMs(override?: number): number {
    if (override != null && Number.isFinite(override) && override > 0)
      return override
    const sched = this.scheduledFinalizeDurationMs
    if (sched != null && Number.isFinite(sched) && sched > 0) return sched
    if (this.tricksDurationMs > 0) {
      const scaled = Math.round(this.tricksDurationMs * 0.2)
      // Минимум 1500 мс: при коротком T кривая `(1-t)^(exp-1)` сжимается во времени
      // и торможение визуально превращается в удар, даже если exp идеальный.
      return Math.min(15_000, Math.max(1500, scaled))
    }
    // Финальный fallback тоже не <1500 мс — иначе SLOWING схлопывается в один кадр.
    return 1500
  }

  // ─── Per-frame advance ───────────────────────────────────────────────────

  /**
   * Advance physics by one tick frame.
   * Computes new base rotation (SLOWING curve or idle spin) and decays trick overlays.
   * Returns updated rotation, trick offset, and whether physics needs another RAF frame.
   *
   * `nowMs` is wall-clock time required for TRICKS velocity decay alignment.
   */
  advance(
    now: number,
    nowMs: number,
    partyCount: number,
    hasSectorTween: boolean
  ): { rotationDeg: number; trickOffset: number; physicsNeedsTick: boolean } {
    // ── Angle computation ──────────────────────────────────────────────────
    let angle = this.currentRotationDeg
    if (this.finalizeState) {
      angle = computeSlowingPosition(this.finalizeState, now)
      if (now >= this.finalizeState.startedAt + this.finalizeState.durationMs) {
        angle = this.finalizeState.targetDeg
        this.finalizeState = null
        this.isFinalizing = false
      }
    } else if (
      partyCount > 0 &&
      !hasSectorTween &&
      !this.roundResolved &&
      this.allowIdleSpin
    ) {
      const dt = now - this.lastTickTime
      if (this.isSpinningPhase) {
        const vel = this.getIdleSpinAngularVelDegPerMs(now, nowMs)
        this.spinAngularVelDegPerMs = vel
        angle += vel * dt
      }
    }
    this.currentRotationDeg = angle

    // ── Trick overlay decay ────────────────────────────────────────────────
    // Skip during SLOWING: power curve owns motion — don't stack trick tails on top.
    if (!this.finalizeState) {
      const spinS = getPvpRouletteRuntime().WHEEL_VISUAL_SPIN_SIGN
      if (this.trickSpeedBoost) {
        const dt = now - this.lastTickTime
        const { remainingMs, durationMs, peakDegPerMs } = this.trickSpeedBoost
        const k = Math.max(0, Math.min(1, remainingMs / durationMs))
        const freezeMul =
          this.trickSpeedDampen != null
            ? getPvpRouletteRuntime().TRICK_FREEZE_FACTOR
            : 1
        this.trickPermanentOffsetDeg +=
          spinS * peakDegPerMs * k * dt * freezeMul
        this.trickSpeedBoost.remainingMs -= dt
        if (this.trickSpeedBoost.remainingMs <= 0) this.trickSpeedBoost = null
      }
      if (this.trickSpeedDampen) {
        const dt = now - this.lastTickTime
        this.trickSpeedDampen.remainingMs -= dt
        if (this.trickSpeedDampen.remainingMs <= 0) this.trickSpeedDampen = null
      }
    }

    const physicsNeedsTick =
      this.finalizeState != null ||
      (this.allowIdleSpin &&
        !this.roundResolved &&
        partyCount > 0 &&
        (this.isSpinningPhase ||
          this.trickSpeedBoost != null ||
          this.trickSpeedDampen != null))

    return {
      rotationDeg: angle,
      trickOffset: this.trickPermanentOffsetDeg,
      physicsNeedsTick,
    }
  }
}
